import { Link } from "wouter";
import ScreenHeader from "@/components/mnf/ScreenHeader";
import { GlassCard, PillTag, Btn, Avatar, SectionTitle } from "@/components/mnf/UI";
import Icon from "@/components/mnf/Icon";

/* ---- A garden of seeds the sister has planted (referrals reframed) ----- */

interface Seed {
  name: string;
  initials: string;
  joined: string;
  status: "rooted" | "flourishing" | "tending";
  ringColor: string;
  tier: "Seedling" | "Rooted" | "Flourishing";
}

const SEEDS: Seed[] = [
  { name: "Rebecca H.", initials: "RH", joined: "3 weeks ago",  status: "flourishing", tier: "Flourishing", ringColor: "rgba(192,120,74,0.55)" },
  { name: "Joanna P.",  initials: "JP", joined: "2 months ago", status: "rooted",      tier: "Rooted",      ringColor: "rgba(168,212,184,0.55)" },
  { name: "Bethany C.", initials: "BC", joined: "5 days ago",   status: "tending",     tier: "Seedling",    ringColor: "rgba(127,119,221,0.55)" },
];

const statusTone: Record<Seed["status"], { bg: string; color: string; label: string }> = {
  flourishing: { bg: "rgba(160,120,32,0.16)", color: "var(--mnf-gold)", label: "Flourishing" },
  rooted:      { bg: "rgba(168,212,184,0.22)", color: "#2C6248",        label: "Rooted" },
  tending:     { bg: "rgba(127,119,221,0.16)", color: "var(--mnf-purple)", label: "Tending" },
};

export default function ShareJourney() {
  return (
    <div className="pb-6">
      <ScreenHeader
        variant="forest"
        eyebrow="Share Your Journey"
        title={<>Plant seeds,<br /><span className="italic">grow a garden</span></>}
        subtitle={<>"One plants, another waters, but God gives the growth." <span style={{ opacity: 0.75 }}>1 Corinthians 3:6</span></>}
        trailing={
          <Link href="/" className="no-underline">
            <button
              data-testid="button-back"
              className="rounded-full flex items-center justify-center"
              style={{
                width: 36, height: 36,
                background: "rgba(245,240,232,0.14)",
                border: "1px solid rgba(245,240,232,0.18)",
                color: "#F5F0E8",
              }}
            >
              <Icon name="close" size={18} />
            </button>
          </Link>
        }
      >
        <div className="flex items-center gap-2 flex-wrap">
          <PillTag tone="gold">3 seeds planted</PillTag>
          <PillTag tone="fiber">2 rooted</PillTag>
          <PillTag tone="coral">$94 earned</PillTag>
        </div>
      </ScreenHeader>

      <div className="px-5 -mt-3 space-y-4">
        {/* Hero — invitation card */}
        <GlassCard organic padding={20}>
          <p className="mnf-eyebrow" style={{ color: "var(--mnf-t)" }}>Your invitation</p>
          <h3 className="mnf-serif" style={{ fontSize: 24, fontWeight: 500, color: "var(--mnf-fd)", lineHeight: 1.2, marginTop: 6 }}>
            Share Move Nourish Flourish with a sister who needs it.
          </h3>
          <p className="mnf-body" style={{ fontSize: 13.5, color: "var(--mnf-tg)", lineHeight: 1.55, marginTop: 10 }}>
            Every sister who joins through your invitation roots in your garden. You both receive a month of grace on us when she stays through her first 30 days.
          </p>
          <div
            className="flex items-center gap-3 mt-4 p-3"
            style={{
              background: "rgba(245,240,232,0.6)",
              borderRadius: 16,
              border: "1px dashed rgba(30,74,53,0.18)",
            }}
          >
            <div
              className="flex items-center justify-center shrink-0"
              style={{ width: 36, height: 36, borderRadius: 12, background: "var(--mnf-f)", color: "#F5F0E8" }}
            >
              <Icon name="link" size={18} />
            </div>
            <div className="flex-1 min-w-0">
              <p className="mnf-eyebrow" style={{ color: "var(--mnf-tg)", fontSize: 9.5 }}>Your sister-link</p>
              <p
                className="mnf-body truncate"
                style={{ fontSize: 13, fontWeight: 600, color: "var(--mnf-fd)" }}
                data-testid="text-share-link"
              >
                mnf.app/sister/hannah
              </p>
            </div>
            <button
              data-testid="button-copy-link"
              className="shrink-0 mnf-body transition-all hover-elevate"
              style={{
                padding: "7px 12px", borderRadius: 999,
                background: "var(--mnf-f)", color: "#F5F0E8",
                fontSize: 10.5, fontWeight: 700, letterSpacing: "0.06em", textTransform: "uppercase",
              }}
            >
              Copy
            </button>
          </div>
          <div className="grid grid-cols-3 gap-2 mt-3">
            <Btn variant="ghost" size="md">
              <Icon name="forum" size={16} /> Text
            </Btn>
            <Btn variant="ghost" size="md">
              <Icon name="mail" size={16} /> Email
            </Btn>
            <Btn variant="coral" size="md">
              <Icon name="ios_share" size={16} /> Share
            </Btn>
          </div>
        </GlassCard>

        {/* Seeds planted */}
        <SectionTitle
          eyebrow="Your garden"
          title="Seeds you've planted"
          trailing={<span style={{ color: "var(--mnf-t)" }}>{SEEDS.length} sisters</span>}
        />
        <div className="space-y-3">
          {SEEDS.map((s) => {
            const tone = statusTone[s.status];
            return (
              <GlassCard key={s.name} padding={16}>
                <div className="flex items-start gap-3">
                  <Avatar initials={s.initials} size={42} ringColor={s.ringColor} />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between gap-2">
                      <p className="mnf-body" style={{ fontSize: 13.5, fontWeight: 700, color: "var(--mnf-fd)" }}>{s.name}</p>
                      <span
                        className="mnf-body shrink-0"
                        style={{
                          padding: "3px 9px", borderRadius: 999,
                          background: tone.bg, color: tone.color,
                          fontSize: 10, fontWeight: 700, letterSpacing: "0.06em", textTransform: "uppercase",
                        }}
                      >
                        {tone.label}
                      </span>
                    </div>
                    <p className="mnf-body" style={{ fontSize: 11.5, color: "var(--mnf-tg)", marginTop: 3 }}>
                      {s.tier} · joined {s.joined}
                    </p>
                  </div>
                </div>
              </GlassCard>
            );
          })}
        </div>

        {/* Rewards */}
        <SectionTitle eyebrow="Grace earned" title="Your harvest" />
        <GlassCard padding={18}>
          <div className="space-y-3">
            {[
              { label: "Months of grace earned", value: "2", unit: "mo", note: "applied to your next renewal", tone: "var(--mnf-t)" },
              { label: "Sister credit balance",  value: "$94", unit: "",   note: "redeemable on retreats &amp; resources", tone: "var(--mnf-gold)" },
              { label: "Garden ranking",          value: "Top 8%", unit: "", note: "of sisters this season", tone: "var(--mnf-purple)" },
            ].map((r, i) => (
              <div
                key={r.label}
                className="flex items-center justify-between gap-3"
                style={{
                  paddingTop: i ? 12 : 0,
                  borderTop: i ? "1px solid rgba(30,74,53,0.06)" : undefined,
                }}
              >
                <div>
                  <p className="mnf-eyebrow" style={{ color: "var(--mnf-tg)" }}>{r.label}</p>
                  <p
                    className="mnf-body italic"
                    style={{ fontSize: 12, color: "var(--mnf-tg)", marginTop: 2 }}
                    dangerouslySetInnerHTML={{ __html: r.note }}
                  />
                </div>
                <p
                  className="mnf-serif shrink-0"
                  style={{ fontSize: 22, fontWeight: 500, color: r.tone, lineHeight: 1 }}
                >
                  {r.value}
                  {r.unit && (
                    <span className="mnf-body" style={{ fontSize: 11, color: "var(--mnf-tg)", marginLeft: 3, fontWeight: 500 }}>
                      {r.unit}
                    </span>
                  )}
                </p>
              </div>
            ))}
          </div>
        </GlassCard>

        {/* Verse close */}
        <GlassCard
          organic
          padding={20}
          style={{
            background: "linear-gradient(135deg, rgba(192,120,74,0.10) 0%, rgba(245,240,232,0.65) 100%)",
            textAlign: "center",
          }}
        >
          <span className="mnf-serif italic" style={{ fontSize: 36, color: "var(--mnf-t)", lineHeight: 1, display: "block" }}>“</span>
          <p
            className="mnf-serif italic"
            style={{ fontSize: 16, color: "var(--mnf-fd)", lineHeight: 1.5, marginTop: 4 }}
          >
            A generous person will prosper; whoever refreshes others will be refreshed.
          </p>
          <p
            className="mnf-eyebrow"
            style={{ color: "var(--mnf-t)", marginTop: 10 }}
          >
            Proverbs 11:25
          </p>
        </GlassCard>
      </div>
    </div>
  );
}
